import { useState, useEffect, useRef, useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import imgImagePic from "../assets/962ef5add980d65c7efa5147f6451803674674bf.png";
import { motion, useScroll, useTransform, AnimatePresence, useMotionValue, useAnimationFrame } from "motion/react";

import { SparklingStar } from "./ui/SparklingStar";
import {
  SocialButton,
  InstagramIcon,
  LinkedInIcon,
  GitHubIcon,
} from "./ui/SocialButton";

// Brand constants
const THEME = {
  accentGreen: "#01E12D",
  primaryDark: "#141414",
  inputBg: "#e7f1f1",
  buttonGreen: "#10fd02",
  buttonText: "#003e5b",
  highlightRed: "#f42536",
  highlightBlue: "#256af4",
  highlightGreen: "#22c365",
} as const;

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/geekcow/",
  linkedin: "https://www.linkedin.com/in/olgamolinaleche/",
  github: "https://github.com/omleche",
} as const;

const STEPS = [
  { id: "name",   label: "Name",    type: "text"   },
  { id: "email",  label: "Email",   type: "email"  },
  { id: "scope",  label: "Scope",   type: "multi"  },
  { id: "budget", label: "Budget",  type: "single" },
  { id: "msg",    label: "Message", type: "area"   },
  { id: "review", label: "Review",  type: "review" },
] as const;

type StepId = (typeof STEPS)[number]["id"];
type FormData = { name: string; email: string; scope: string[]; budget: string; msg: string };

type ScopeOption = { label: string; color: string };

/** The profile face's Scope options — the default. The agency passes its own
 *  service buckets via the `scopeOptions` prop (see AgencyPage). */
const DEFAULT_SCOPE_OPTIONS: ScopeOption[] = [
  { label: "Website",        color: THEME.highlightGreen },
  { label: "Web App",        color: THEME.highlightBlue  },
  { label: "Branding",       color: THEME.highlightRed   },
  { label: "Design System",  color: "#ffc803"            },
  { label: "Security Audit", color: THEME.accentGreen    },
];
const BUDGET_OPTIONS = ["€1–3k", "€3–8k", "€8k+", "Let's talk"];



interface ContactSectionProps {
  /** Voice differs per face — the agency speaks as "we", the profile as "I". */
  heading?: string;
  intro?: React.ReactNode;
  note?: string;
  successNote?: string;
  /** Set false when a <SiteFooter/> follows this section. */
  showFooter?: boolean;
  /**
   * The orbiting illustration. Defaults to Olga's portrait, which is right for
   * the professional face; the agency passes the GeekCow mascot so the contact
   * section matches its own hero.
   */
  illustration?: string;
  illustrationAlt?: string;
  /**
   * "circle" crops a square portrait into a round frame (the default).
   * "free" fits the whole artwork, for cut-out illustrations like the mascot —
   * it is 2:3, so a round object-cover frame would slice off its horns and chin.
   */
  illustrationShape?: "circle" | "free";
  /**
   * The Scope step's options. Defaults to a project-type set; the agency passes
   * its own services, and the profile passes engagement models, so the form
   * collects the answer that fits the face.
   */
  scopeOptions?: ScopeOption[];
  /** Heading for the Scope step. Default asks about a project ("what are we building?"). */
  scopeQuestion?: React.ReactNode;
  /** Short label for the Scope step, shown in the progress bar and review. */
  scopeLabel?: string;
  /** Heading for the Budget step. */
  budgetQuestion?: React.ReactNode;
  /** Short label for the Budget step, shown in the progress bar and review. */
  budgetLabel?: string;
  /** Options for the Budget step. Default is project budget bands; the profile passes a timeline. */
  budgetOptions?: string[];
}

const DEFAULT_INTRO = (
  <>
    <span className="text-[#141414]">I am </span>
    <span className="text-[#f42536]">ready </span>
    <span className="text-[#141414]">and </span>
    <span className="text-[#256af4]">excited </span>
    <span className="text-[#141414]">to start </span>
    <span className="text-[#22c365]">your project.</span>
  </>
);

export function ContactSection({
  heading = "Contact Me",
  intro = DEFAULT_INTRO,
  note = "Fill out the form — I'll beam back within a day.",
  successNote = "Message received loud and clear — I'll beam back within a day.",
  showFooter = true,
  illustration = imgImagePic,
  illustrationAlt = "Character portrait",
  illustrationShape = "circle",
  scopeOptions = DEFAULT_SCOPE_OPTIONS,
  scopeQuestion,
  scopeLabel = "Scope",
  budgetQuestion,
  budgetLabel = "Budget",
  budgetOptions = BUDGET_OPTIONS,
}: ContactSectionProps = {}) {
  /** Label for a step, honouring the per-face overrides for scope/budget. */
  const labelFor = (id: StepId, fallback: string) =>
    id === "scope" ? scopeLabel : id === "budget" ? budgetLabel : fallback;
  const isCircle = illustrationShape === "circle";
  const frameClass = isCircle ? "rounded-full" : "";
  const frameShadow = isCircle ? "0px 25px 50px 0px rgba(0,0,0,0.25)" : "none";
  /**
   * A square frame would letterbox a 2:3 cut-out down to ~169px wide. The free
   * shape is therefore sized by WIDTH with height following the artwork, using
   * the same widths as the hero mascot so the two read as the same illustration.
   */
  const frameSize = isCircle ? "w-[254px] h-[254px]" : "w-[270px] xl:w-[320px] h-auto";
  const frameSizeMobile = isCircle ? "w-full h-full" : "w-[150px] h-auto";
  const imageClass = isCircle ? "w-full h-full rounded-full object-cover" : "w-full h-auto drop-shadow-2xl";
  /** Half the frame width, to keep the orbit centred on the card. */
  const frameHalfWidth = isCircle ? "127px" : "135px";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const circleRotate1 = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const circleRotate2 = useTransform(scrollYProgress, [0, 1], [0, -270]);
  const circleRotate3 = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const circleRotate4 = useTransform(scrollYProgress, [0, 1], [0, -90]);

  // Orbit animation around the card — slow, wide ellipse that never overlaps the card.
  // The tall free-shape mascot (agency) gets a much tighter orbit than the round
  // portrait (profile): on its wide arc the mascot swung far enough that the
  // section's overflow-hidden clipped it out of view near the bottom edge. A
  // smaller ellipse keeps the whole illustration on-screen at every point.
  const orbitRadiusX = isCircle ? 560 : 180;
  const orbitRadiusY = isCircle ? 320 : 70;
  const orbitAngle = useMotionValue(0);
  useAnimationFrame((t) => { orbitAngle.set(t * 0.00008); }); // ~87s per orbit
  const orbitX = useTransform(orbitAngle, (a) => Math.cos(a) * orbitRadiusX);
  const orbitY = useTransform(orbitAngle, (a) => Math.sin(a) * orbitRadiusY);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left - rect.width  / 2) / rect.width,
          y: (e.clientY - rect.top  - rect.height / 2) / rect.height,
        });
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // ── Multi-step form state ──────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [direction,   setDirection]   = useState(1);
  const [formData, setFormData] = useState<FormData>({ name: "", email: "", scope: [], budget: "", msg: "" });
  const [stepError,    setStepError]   = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError,  setSubmitError]  = useState<string | null>(null);
  const [isSent,       setIsSent]       = useState(false);
  /**
   * False until the visitor moves between steps themselves. The step field is
   * only focused once this flips, because focusing on the very first render
   * makes the browser scroll the whole page down to the form — the page opened
   * on the contact card instead of the hero.
   */
  const hasNavigatedSteps = useRef(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const API_URL = import.meta.env.VITE_API_URL || "https://api.geekcow.com";
  const LAST = STEPS.length - 1;

  const validateCurrentStep = useCallback((): boolean => {
    switch (STEPS[currentStep].id) {
      case "name":  return formData.name.trim().length > 0;
      case "email": return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
      case "msg":   return formData.msg.trim().length > 2;
      default:      return true; // scope, budget, review are optional
    }
  }, [currentStep, formData]);

  const transmit = useCallback(async () => {
    setSubmitError(null);
    if (!executeRecaptcha) { setSubmitError("reCAPTCHA not ready. Please try again."); return; }
    setIsSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha("contact_form");
      const response = await fetch(`${API_URL}/api/email/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:          formData.name,
          email:         formData.email,
          company:       formData.scope.join(", "),
          inquiryType:   formData.budget || "General Inquiry",
          message:       formData.msg,
          recaptchaToken,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to send message");
      setIsSent(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [executeRecaptcha, formData, API_URL]);

  const goNext = useCallback(() => {
    if (currentStep < LAST) {
      if (!validateCurrentStep()) { setStepError(true); return; }
      hasNavigatedSteps.current = true;
      setStepError(false); setDirection(1); setCurrentStep(s => s + 1);
    } else {
      transmit();
    }
  }, [currentStep, LAST, validateCurrentStep, transmit]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      hasNavigatedSteps.current = true;
      setStepError(false); setDirection(-1); setCurrentStep(s => s - 1);
    }
  }, [currentStep]);

  // Enter key advances steps (but not inside textarea)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
        e.preventDefault(); goNext();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [goNext]);

  const step = STEPS[currentStep];

  return (
    <section
      ref={sectionRef}
      id="contact"
      aria-labelledby="contact-heading"
      /*
        Below `lg` this section lays out in normal flow, so it is exactly as tall
        as the heading + portrait + form card. It used to keep the desktop's
        1180px floor while every child was absolutely positioned, which left
        roughly 400px of empty background between the form and the footer on a
        phone. From `lg` the absolute composition (orbiting portrait, offset
        intro, overlapping card) comes back and the floor with it.
      */
      className="relative w-full overflow-hidden flex flex-col items-center gap-8 px-4 pt-16 pb-16 lg:block lg:gap-0 lg:px-0 lg:pb-0 lg:pt-0 lg:min-h-[1160px]"
      style={{ perspective: "1000px" }}
    >
      {/* ── Floating concentric circles ─────────────────────────────────── */}
      {([
        { rotate: circleRotate1, size: "366.5px", sizeMd: "733px",  vb: "736",  r: "366.5", cx: "368" },
        { rotate: circleRotate2, size: "522.5px", sizeMd: "1045px", vb: "1048", r: "522.5", cx: "524" },
        { rotate: circleRotate3, size: "664px",   sizeMd: "1328px", vb: "1331", r: "664",   cx: "665.5" },
        { rotate: circleRotate4, size: "869.5px", sizeMd: "1739px", vb: "1742", r: "869.5", cx: "871" },
      ] as const).map((c, i) => (
        <motion.div key={i} className="absolute left-1/2 top-[40%] md:top-1/2 pointer-events-none"
          style={{ rotate: c.rotate, x: "-50%", y: "-50%", transformStyle: "preserve-3d" }}>
          <svg className={`block w-[${c.size}] h-[${c.size}] md:w-[${c.sizeMd}] md:h-[${c.sizeMd}]`} fill="none" preserveAspectRatio="none" viewBox={`0 0 ${c.vb} ${c.vb}`}>
            <motion.circle
              cx={c.cx} cy={c.cx} r={c.r}
              strokeDasharray="14 14" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" fill="none"
              stroke="#01E12D"
            />
          </svg>
        </motion.div>
      ))}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="relative z-10 w-full lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:top-[100px]">
        <h2
          id="contact-heading"
          className="font-['Outfit',sans-serif] font-black text-[32px] md:text-7xl lg:text-[96px] leading-tight tracking-[-1px] md:tracking-[-2.4px] text-center text-[#141414]"
        >
          {heading}
        </h2>
      </header>

      {/* ── Character image — below `lg` ─────────────────────────────────────
          In flow, not pinned: it is a step in the column rather than a layer
          over it. `lg:hidden` rather than `md:hidden`, because the orbiting
          desktop portrait only starts at `lg` — tablets were showing neither. */}
      <div
        className={`relative z-20 lg:hidden ${
          isCircle ? "w-[127px] h-[127px]" : "w-[150px] h-auto"
        }`}
      >
        <div className={`relative ${frameSizeMobile} ${frameClass}`}
          style={{
            transform: "rotate(-8.654deg)",
            boxShadow: isCircle ? "0px 12.5px 25px 0px rgba(0,0,0,0.25)" : "none",
          }}>
          <img alt={illustrationAlt} className={`block ${imageClass}`} src={illustration} />
        </div>
      </div>

      {/* ── Intro + note — below `lg` ────────────────────────────────────────
          The desktop copy of this sits beside the portrait and was `hidden`
          everywhere else, so on a phone the section opened straight onto a form
          with no sentence explaining it. Same words, stacked. */}
      <div className="relative z-10 flex flex-col items-center gap-3 text-center max-w-[520px] lg:hidden">
        <p className="font-['Outfit',sans-serif] font-bold text-[26px] sm:text-[32px] leading-[1.2]">
          {intro}
        </p>
        <p className="font-['Outfit',sans-serif] font-medium text-[16px] sm:text-lg leading-[1.5] text-[#3a3a3a]">
          {note}
        </p>
      </div>

      {/* ── Desktop intro text + stars ───────────────────────────────────── */}
      <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 lg:left-[417px] lg:translate-x-0 top-[200px] w-full max-w-[1086px] px-4 lg:px-0 flex-col gap-8 items-center lg:items-start justify-center z-10">
        <div
          className="flex flex-col justify-center leading-tight lg:leading-[64px] text-center gap-4 rounded-2xl px-6 py-4"
        >
          <p className="font-['Outfit',sans-serif] font-bold text-2xl md:text-4xl lg:text-[48px]">
            {intro}
          </p>
          <p
            className="font-['Outfit',sans-serif] font-medium text-lg md:text-xl lg:text-2xl text-[#3a3a3a]"
          >
            {note}
          </p>
        </div>
        <div className="hidden md:block">
          <SparklingStar className="left-[78%] top-[-80%] w-[32px] h-[24.5333px]" duration={3}>
            <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 32 24.5333">
              <motion.path d="M16 0L18 10.7333L32 12.2667L18 13.8L16 24.5333L14 13.8L0 12.2667L14 10.7333L16 0Z"
                fill="#141414" />
            </svg>
          </SparklingStar>
          <SparklingStar className="left-[20%] top-[70%] w-[20px] h-[20px]" duration={4} delay={1}>
            <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
              <motion.path d="M10 0L11.25 8.75L20 10L11.25 11.25L10 20L8.75 11.25L0 10L8.75 8.75L10 0Z"
                fill="#141414" />
            </svg>
          </SparklingStar>
          <SparklingStar className="left-[31%] top-[2%] w-[24px] h-[18.4px]" duration={3.5} delay={0.5}>
            <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 18.4">
              <motion.path d="M12 0L13.5 8.05L24 9.2L13.5 10.35L12 18.4L10.5 10.35L0 9.2L10.5 8.05L12 0Z"
                fill="#141414" />
            </svg>
          </SparklingStar>
        </div>
      </div>

      {/* ── Character image — Desktop (orbits behind the card) ───────────── */}
      <motion.div
        className="hidden lg:block absolute z-[5]"
        style={{
          left: `calc(50% - ${frameHalfWidth})`,
          top: "513px",
          x: orbitX,
          y: orbitY,
        }}
      >
        {/* Drag wrapper — offset stacks on top of the orbit */}
        <motion.div
          drag
          dragConstraints={sectionRef}
          dragMomentum={false}
          dragElastic={0.08}
          whileDrag={{ scale: 1.06, cursor: "grabbing" }}
          style={{ cursor: "grab", touchAction: "none" }}
          title="Drag me!"
        >
          <motion.div
            className={`relative ${frameSize} ${frameClass}`}
            style={{ rotate: -8.654, boxShadow: frameShadow }}
            whileHover={{ scale: 1.1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <img
              alt={`${illustrationAlt} — drag me around`}
              className={`block pointer-events-none ${imageClass}`}
              src={illustration}
              style={{ filter: `drop-shadow(${mousePosition.x * 10}px ${mousePosition.y * 10}px 20px rgba(1, 225, 45, 0.3))` }}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* ── Multi-step Transmitter Card ──────────────────────────────────── */}
      <div className="relative w-full max-w-[720px] z-10 lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:top-[430px] lg:w-[calc(100%-2rem)]">
        <motion.div
          className="rounded-3xl border overflow-hidden"
          style={{
            backgroundColor: "#ffffff",
            borderColor: "rgba(0,0,0,0.07)",
            boxShadow: "0 40px 80px -40px rgba(20,30,20,0.4)",
          }}
        >
          {/* Card header */}
          <motion.div
            className="flex items-center justify-between px-6 pt-5 pb-4"
            style={{ borderBottomWidth: 1, borderBottomStyle: "solid", borderBottomColor: "rgba(0,0,0,0.06)" }}
          >
            <div className="flex items-center gap-2.5">
              <motion.div
                className="w-2 h-2 rounded-full bg-[#01E12D] shrink-0"
                style={{ boxShadow: "0 0 8px #01E12D" }}
                animate={{ opacity: [1, 0.2, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase">
                <span className="font-bold tracking-[0.05em] text-[#141414]">GEEKCOW</span>
                <motion.span className="text-[#999]"> // Transmitter</motion.span>
              </span>
            </div>
            <div className="flex items-end gap-[3px] h-5" aria-hidden="true">
              {[0, 0.15, 0.3, 0.45, 0.6].map((delay, i) => (
                <motion.div key={i} className="w-[3px] rounded-sm"
                  style={{ background: "linear-gradient(180deg,#01E12D,#256af4)" }}
                  animate={{ height: ["25%", "100%", "25%"] }}
                  transition={{ duration: 1, delay, repeat: Infinity, ease: "easeInOut" }}
                />
              ))}
            </div>
          </motion.div>

          {/* Step content */}
          <div className="relative px-6 py-5 min-h-[200px] overflow-hidden">
            <AnimatePresence mode="wait" initial={false} custom={direction}>
              {isSent ? (
                <motion.div key="sent" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-6 gap-4 text-center">
                  <div className="relative w-20 h-20 flex items-center justify-center">
                    {[0, 0.6].map((delay, i) => (
                      <motion.div key={i} className="absolute inset-0 rounded-full border-2 border-[#01E12D]"
                        animate={{ scale: [0.5, 1.7], opacity: [0.85, 0] }}
                        transition={{ duration: 1.8, delay, repeat: Infinity, ease: "easeOut" }} />
                    ))}
                    <div className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                      style={{ background: "linear-gradient(135deg,#01E12D,#256af4)" }}>✓</div>
                  </div>
                  <h3 className="font-['Outfit',sans-serif] font-black text-2xl tracking-tight text-[#141414]">
                    Signal transmitted!
                  </h3>
                  <p className="font-['Outfit',sans-serif] text-sm text-[#666] max-w-[28ch] leading-relaxed">
                    {successNote}
                  </p>
                </motion.div>
              ) : (
                <motion.div key={currentStep} custom={direction}
                  initial={{ x: direction * 55, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: direction * -55, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 320, damping: 32 }}>
                  <p className="font-mono text-[10.5px] tracking-[0.2em] uppercase text-[#aaa] mb-3"
                    aria-label={`Step ${currentStep + 1} of ${STEPS.length}`}>
                    {String(currentStep + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                  </p>
                  <StepQuestion stepId={step.id} scopeQuestion={scopeQuestion} budgetQuestion={budgetQuestion} />
                  <div className="mt-5">
                    <StepInput stepId={step.id} formData={formData} setFormData={setFormData}
                      stepError={stepError} setStepError={setStepError} scopeOptions={scopeOptions}
                      budgetOptions={budgetOptions} scopeLabel={scopeLabel} budgetLabel={budgetLabel}
                      autoFocus={hasNavigatedSteps.current} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Card footer — progress + nav */}
          {!isSent && (
            <div className="flex items-center justify-between px-6 pb-5 pt-4"
              style={{ borderTopWidth: 1, borderTopStyle: "solid", borderTopColor: "rgba(0,0,0,0.06)" }}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5" role="tablist" aria-label="Form progress">
                  {STEPS.map((s, i) => (
                    <motion.div key={s.id} role="tab" aria-selected={i === currentStep}
                      aria-label={`Step ${i + 1}: ${labelFor(s.id, s.label)}`} className="h-2 rounded-full"
                      animate={{
                        width: i === currentStep ? 20 : 8,
                        backgroundColor:
                          i < currentStep ? "#141414"
                          : i === currentStep ? "#01E12D"
                          : "#ddd",
                      }}
                      transition={{ duration: 0.3 }} />
                  ))}
                </div>
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#aaa]">
                  {labelFor(step.id, step.label)}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <button type="button" onClick={goBack} disabled={currentStep === 0}
                  className="w-11 h-11 rounded-full border-[1.5px] border-black/[0.14] flex items-center justify-center text-[#141414] font-bold text-xl disabled:opacity-25 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#01E12D] focus:ring-offset-2 hover:scale-105 active:scale-95 transition-transform"
                  aria-label="Go back">
                  ‹
                </button>
                <motion.button type="button" onClick={goNext} disabled={isSubmitting}
                  className="flex items-center gap-2 bg-[#ffc803] text-[#141414] font-['Outfit',sans-serif] font-extrabold text-[13px] tracking-wide px-5 py-[11px] rounded-full shadow-[0_5px_0_-2px_rgba(0,0,0,0.12)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#ffc803] focus:ring-offset-2"
                  whileHover={{ y: -2, boxShadow: "0 9px 0 -2px rgba(0,0,0,0.14)" }}
                  whileTap={{ y: 2, boxShadow: "0 2px 0 -2px rgba(0,0,0,0.10)" }}
                  aria-label={currentStep === LAST ? "Transmit message" : "Continue to next step"}>
                  {isSubmitting ? "Transmitting…" : currentStep === LAST ? "Transmit" : "Continue"}
                  {!isSubmitting && currentStep !== LAST && (
                    <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }} aria-hidden="true">→</motion.span>
                  )}
                </motion.button>
              </div>
            </div>
          )}

          {submitError && (
            <div className="mx-6 mb-5 p-3 rounded-xl text-center font-['Public_Sans',sans-serif] text-sm bg-red-50 text-red-700 border border-red-200">
              {submitError}
            </div>
          )}
        </motion.div>

        <p className="text-center font-mono text-[10px] mt-3 tracking-[0.06em] text-[#aaa]"
          aria-hidden="true">
          press <strong>Enter ↵</strong> to continue · ‹ to go back
        </p>
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────────
          Only rendered when this section is the end of the page. Both faces now
          render <SiteFooter/> below it, so they pass showFooter={false}. */}
      {showFooter && (
        <footer className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col gap-4 md:gap-6 items-center z-10 w-full px-4 text-center">
          <nav aria-label="Social media links" className="flex gap-4">
            <SocialButton href={SOCIAL_LINKS.instagram} ariaLabel="Follow on Instagram"><InstagramIcon /></SocialButton>
            <SocialButton href={SOCIAL_LINKS.linkedin}  ariaLabel="Connect on LinkedIn"><LinkedInIcon /></SocialButton>
            <SocialButton href={SOCIAL_LINKS.github}    ariaLabel="View GitHub profile"><GitHubIcon /></SocialButton>
          </nav>
          <div className="flex flex-col gap-1 items-center">
            <p className="font-['Outfit',sans-serif] text-sm leading-5 text-[#666]">
              KvK# 73006637  All rights reserved.
            </p>
            <p className="font-['Outfit',sans-serif] text-sm leading-5 text-[#666]">
              © {new Date().getFullYear()} GeekCow Creative Studio is a sole trader established in Weesp, Netherlands.
            </p>
          </div>
        </footer>
      )}
    </section>
  );
}

// ── Helper sub-components ─────────────────────────────────────────────────────

function StepQuestion({
  stepId,
  scopeQuestion,
  budgetQuestion,
}: {
  stepId: StepId;
  scopeQuestion?: React.ReactNode;
  budgetQuestion?: React.ReactNode;
}) {
  const questions: Record<StepId, React.ReactNode> = {
    name:   <>First, who's <span className="text-[#f42536]">on the line?</span></>,
    email:  <>Where do I <span className="text-[#256af4]">beam the reply?</span></>,
    scope:  scopeQuestion ?? <>What are we <span className="text-[#22c365]">building?</span></>,
    budget: budgetQuestion ?? <>Ballpark <span style={{ color: "#ffc803" }}>budget?</span></>,
    msg:    <>Tell me about <span className="text-[#01E12D]">the mission.</span></>,
    review: <>Ready to <span className="text-[#256af4]">transmit?</span></>,
  };
  return (
    <h3 className="font-['Outfit',sans-serif] font-black text-[clamp(24px,4vw,36px)] leading-[1.05] tracking-[-0.025em] text-[#141414]">
      {questions[stepId]}
    </h3>
  );
}

function StepInput({
  stepId,
  formData,
  setFormData,
  stepError,
  setStepError,
  scopeOptions,
  budgetOptions,
  scopeLabel,
  budgetLabel,
  autoFocus,
}: {
  stepId: StepId;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  stepError: boolean;
  setStepError: (v: boolean) => void;
  scopeOptions: ScopeOption[];
  budgetOptions: string[];
  scopeLabel: string;
  budgetLabel: string;
  /** Only true once the visitor has stepped through the form themselves. */
  autoFocus: boolean;
}) {
  const inputRef    = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // React's `autoFocus` attribute would fire on mount — including the mount that
  // happens when the page first loads — and drag the viewport to the form. Doing
  // it here instead keeps the caret behaviour for someone working through the
  // steps, and `preventScroll` keeps the page still either way.
  useEffect(() => {
    if (!autoFocus) return;
    (inputRef.current ?? textareaRef.current)?.focus({ preventScroll: true });
  }, [autoFocus, stepId]);

  const underlineInput = [
    "w-full bg-transparent font-['Public_Sans',sans-serif] font-bold text-[clamp(18px,3vw,26px)] outline-none border-b-2 pb-1.5 transition-colors duration-200",
    "text-[#141414] placeholder:text-black/20",
    stepError ? "border-[#f42536]" : "border-black/10 focus:border-[#01E12D]",
  ].join(" ");

  const chipBase = "transparent";
  const chipBorder = "rgba(0,0,0,0.13)";
  const chipText = "#141414";

  if (stepId === "name") return (
    <div>
      <label htmlFor="contact-name" className="sr-only">Your name</label>
      <input id="contact-name" ref={inputRef} type="text" placeholder="Your name" autoComplete="name"
        className={underlineInput} value={formData.name}
        onChange={e => { setFormData(p => ({ ...p, name: e.target.value })); setStepError(false); }} />
      {stepError && <p className="text-[#f42536] text-xs font-semibold mt-2">I need a name to reply to.</p>}
    </div>
  );

  if (stepId === "email") return (
    <div>
      <label htmlFor="contact-email" className="sr-only">Your email</label>
      <input id="contact-email" ref={inputRef} type="email" placeholder="you@company.com" autoComplete="email"
        className={underlineInput} value={formData.email}
        onChange={e => { setFormData(p => ({ ...p, email: e.target.value })); setStepError(false); }} />
      {stepError && <p className="text-[#f42536] text-xs font-semibold mt-2">That email doesn't look right.</p>}
    </div>
  );

  if (stepId === "scope") return (
    <div className="flex flex-wrap gap-2">
      {scopeOptions.map(opt => {
        const on = formData.scope.includes(opt.label);
        return (
          <button key={opt.label} type="button"
            onClick={() => setFormData(p => ({
              ...p, scope: on ? p.scope.filter(s => s !== opt.label) : [...p.scope, opt.label],
            }))}
            className="px-4 py-2 rounded-full border-[1.5px] text-[13px] font-semibold font-['Outfit',sans-serif] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1"
            style={{
              borderColor: on ? opt.color : chipBorder,
              background:  on ? opt.color : chipBase,
              color:       on ? "#fff" : chipText,
            }}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  if (stepId === "budget") return (
    <div className="flex flex-wrap gap-2">
      {budgetOptions.map(opt => {
        const on = formData.budget === opt;
        return (
          <button key={opt} type="button"
            onClick={() => setFormData(p => ({ ...p, budget: on ? "" : opt }))}
            className="px-4 py-2 rounded-full border-[1.5px] text-[13px] font-semibold font-['Outfit',sans-serif] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#ffc803] focus:ring-offset-1"
            style={{
              borderColor: on ? "#141414" : chipBorder,
              background:  on ? "#141414" : chipBase,
              color:       on ? "#ffc803" : chipText,
            }}>
            {opt}
          </button>
        );
      })}
    </div>
  );

  if (stepId === "msg") return (
    <div>
      <label htmlFor="contact-msg" className="sr-only">Your message</label>
      <textarea id="contact-msg" ref={textareaRef}
        placeholder="Goals, timeline, anything I should know…"
        className={[
          "w-full rounded-2xl p-4 font-['Public_Sans',sans-serif] font-semibold text-[14px] outline-none resize-none min-h-[100px] leading-relaxed transition-all",
          "bg-[#e7f1f1] text-[#141414] placeholder:text-black/30",
          stepError ? "ring-2 ring-[#f42536]" : "focus:ring-2 focus:ring-[#01E12D]",
        ].join(" ")}
        value={formData.msg}
        onChange={e => { setFormData(p => ({ ...p, msg: e.target.value })); setStepError(false); }} />
      {stepError && <p className="text-[#f42536] text-xs font-semibold mt-2">Give me a line or two to work with.</p>}
    </div>
  );

  if (stepId === "review") return (
    <div className="flex flex-col gap-2.5">
      {([
        { key: "Name",       val: formData.name },
        { key: "Email",      val: formData.email },
        { key: scopeLabel,   val: formData.scope.join(", ") },
        { key: budgetLabel,  val: formData.budget },
        { key: "Message",    val: formData.msg },
      ] as { key: string; val: string }[]).map(({ key, val }) => (
        <div key={key} className="flex gap-3 pb-2.5 text-sm"
          style={{ borderBottom: "1px dashed rgba(0,0,0,0.08)" }}>
          <span className="font-mono text-[10px] tracking-[0.15em] uppercase min-w-[66px] pt-0.5"
            style={{ color: "#aaa" }}>{key}</span>
          <span className="font-['Public_Sans',sans-serif] font-semibold flex-1 break-all leading-snug"
            style={{ color: val ? "#141414" : "rgba(0,0,0,0.25)" }}>
            {val || "—"}
          </span>
        </div>
      ))}
    </div>
  );

  return null;
}

